import { useCallback, useEffect, useMemo, useState } from 'react'

import { pb } from '../../../pocketbase'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  COL_CONSUMABLE_CATEGORIES,
  COL_CONSUMABLES,
  COL_PRODUCT_CATEGORIES,
  COL_PRODUCTS,
} from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { useAuth } from '../../../context/AuthContext'
import type { ConsumableCategoryRecord, ProductCategoryRecord } from '../../../types/records'
import { FormField, formInputProps } from '../../../components/ui/FormField'
import { Modal, ModalActions, modalBtn } from '../../../components/ui/Modal'
import {
  alert as alertClass,
  btn,
  cn,
  sectionTitleClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../../lib/ui'

type Kind = 'consumables' | 'products'

type CatRow = ConsumableCategoryRecord | ProductCategoryRecord

export function CategoriesAdminPage({ kind }: { kind: Kind }) {
  const { can } = useAuth()
  const collection = kind === 'consumables' ? COL_CONSUMABLE_CATEGORIES : COL_PRODUCT_CATEGORIES
  const usageCollection = kind === 'consumables' ? COL_CONSUMABLES : COL_PRODUCTS
  const usageField = kind === 'consumables' ? 'consumable_category' : 'product_category'
  const canWrite =
    kind === 'consumables'
      ? can(PERMISSION_KEYS.CONSUMABLES_WRITE)
      : can(PERMISSION_KEYS.PRODUCTS_WRITE)

  const [rows, setRows] = useState<CatRow[]>([])
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formName, setFormName] = useState('')
  const [formSort, setFormSort] = useState('0')
  const [formParent, setFormParent] = useState('')

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(collection).getFullList<CatRow>({ sort: 'sort,name' })
      setRows(list)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [collection])

  useEffect(() => {
    void load()
  }, [load])

  const nameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of rows) {
      m.set(r.id, r.name)
    }
    return m
  }, [rows])

  function openCreate() {
    setEditingId(null)
    setFormName('')
    setFormSort(String(rows.reduce((mx, r) => Math.max(mx, Number(r.sort) || 0), 0) + 1))
    setFormParent('')
    setModal(true)
  }

  function openEdit(row: CatRow) {
    setEditingId(row.id)
    setFormName(row.name)
    setFormSort(String(row.sort ?? 0))
    setFormParent(typeof row.parent === 'string' ? row.parent : '')
    setModal(true)
  }

  async function onSaveModal(e: React.FormEvent) {
    e.preventDefault()
    const name = formName.trim()
    if (!name) return
    const sortNum = Number(formSort.replace(',', '.'))
    const sort = Number.isFinite(sortNum) ? sortNum : 0
    const body: Record<string, unknown> = { name, sort }
    if (formParent) body.parent = formParent
    else body.parent = null
    try {
      if (editingId) {
        await pb.collection(collection).update(editingId, body)
      } else {
        await pb.collection(collection).create(body)
      }
      setModal(false)
      await load()
    } catch (e: unknown) {
      alert(pocketbaseErrorMessage(e, 'Ошибка'))
    }
  }

  async function removeRow(row: CatRow) {
    if (!canWrite) return
    if (!window.confirm(`Удалить категорию «${row.name}»?`)) return
    try {
      const kids = await pb.collection(collection).getFullList({ filter: `parent="${row.id}"` })
      if (kids.length > 0) {
        alert('Сначала удалите или перенесите вложенные категории.')
        return
      }
      const used = await pb.collection(usageCollection).getList(1, 1, { filter: `${usageField}="${row.id}"` })
      if (used.totalItems > 0) {
        alert(
          kind === 'consumables'
            ? 'Категория привязана к расходникам — снимите привязку в справочнике расходников.'
            : 'Категория привязана к продукции — снимите привязку в карточках продукции.',
        )
        return
      }
      await pb.collection(collection).delete(row.id)
      await load()
    } catch (e: unknown) {
      alert(pocketbaseErrorMessage(e, 'Ошибка'))
    }
  }

  const flatParentOptions = useMemo(() => {
    const out: { id: string; label: string }[] = []
    const byParent = new Map<string, CatRow[]>()
    for (const r of rows) {
      const p = typeof r.parent === 'string' && r.parent ? r.parent : ''
      const arr = byParent.get(p) ?? []
      arr.push(r)
      byParent.set(p, arr)
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0) || a.name.localeCompare(b.name, 'ru'))
    }
    const walk = (parentKey: string, depth: number) => {
      for (const n of byParent.get(parentKey) ?? []) {
        if (editingId && n.id === editingId) continue
        out.push({ id: n.id, label: `${'—'.repeat(depth)} ${n.name}`.trim() })
        walk(n.id, depth + 1)
      }
    }
    walk('', 0)
    return out
  }, [rows, editingId])

  const sectionTitle = kind === 'consumables' ? 'Категории комплектации' : 'Категории продукции'

  return (
    <div>
      <h2 className={sectionTitleClass}>{sectionTitle}</h2>
      {err && <div className={alertClass('err')}>{err}</div>}
      <div className={toolbarClass}>
        {canWrite && (
          <button type="button" className={btn('primary')} onClick={openCreate}>
            Добавить категорию
          </button>
        )}
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
      </div>
      <div className={tableWrapClass}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Название</th>
              <th className={tableHeadClass}>Родитель</th>
              <th className={tableHeadClass}>Порядок</th>
              {canWrite && <th className={tableHeadClass} />}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={canWrite ? 4 : 3}>
                  <em>Загрузка…</em>
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={canWrite ? 4 : 3}>
                  <em>Нет категорий</em>
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const pid = typeof row.parent === 'string' ? row.parent : ''
                return (
                  <tr key={row.id} className={tableRowHoverClass}>
                    <td className={tableCellClass}>{row.name}</td>
                    <td className={tableCellClass}>{pid ? (nameById.get(pid) ?? pid) : '—'}</td>
                    <td className={tableCellClass}>{row.sort ?? 0}</td>
                    {canWrite && (
                      <td className={cn(tableCellClass, 'whitespace-nowrap')}>
                        <button type="button" className={btn('ghost', 'sm')} onClick={() => openEdit(row)}>
                          Изменить
                        </button>
                        <button
                          type="button"
                          className={cn(btn('danger', 'sm'), 'ml-1.5')}
                          onClick={() => void removeRow(row)}
                        >
                          Удалить
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal
          title={editingId ? 'Редактирование категории' : 'Новая категория'}
          titleId="cat-adm-title"
          onClose={() => setModal(false)}
        >
          <form onSubmit={(e) => void onSaveModal(e)} className="flex flex-col gap-4">
            <FormField label="Название" htmlFor="cat-adm-name">
              <input
                id="cat-adm-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                maxLength={200}
                required
                autoFocus
                {...formInputProps()}
              />
            </FormField>
            <FormField label="Порядок сортировки" htmlFor="cat-adm-sort">
              <input
                id="cat-adm-sort"
                type="text"
                inputMode="numeric"
                value={formSort}
                onChange={(e) => setFormSort(e.target.value)}
                {...formInputProps()}
              />
            </FormField>
            <FormField label="Родитель" htmlFor="cat-adm-parent">
              <select
                id="cat-adm-parent"
                value={formParent}
                onChange={(e) => setFormParent(e.target.value)}
                {...formInputProps()}
              >
                <option value="">Корень</option>
                {flatParentOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>
            <ModalActions>
              <button type="submit" className={modalBtn('primary')}>
                Сохранить
              </button>
              <button type="button" className={modalBtn('ghost')} onClick={() => setModal(false)}>
                Отмена
              </button>
            </ModalActions>
          </form>
        </Modal>
      )}
    </div>
  )
}
